import { useState, useEffect, type ReactNode } from 'react';
import type { Canvas } from 'fabric';
import type { TPointerEvent } from 'fabric';
import { useToolContext } from '@/context/ToolContext';
import addItem from '@/utils/addItem';
import addShirt from '@/utils/addShirt';

let dynamicAsset: HTMLElement | undefined; // not really react-y — we modify the dom directly here

interface MainProps {
  canvas: Canvas;
  iconType: string;
  canvasEl?: HTMLCanvasElement | null;
  asideDrag: (style: string) => void;
  children: ReactNode;
}

// Permissive shape for the React mouse/touch synthetic events these drag handlers read. The `touches`
// vs `pageX` branch is runtime-guarded; the drag/drop behaviour is covered by the golden net.
interface DragSyntheticEvent {
  touches?: { length: number; 0: { pageX: number; pageY: number } };
  changedTouches?: { 0: { pageX: number; pageY: number } };
  pageX: number;
  pageY: number;
  target: HTMLElement;
  preventDefault: () => void;
}

const Main = (props: MainProps) => {
  const { canvas, iconType, canvasEl, asideDrag, children } = props;

  const [isDragging, setIsDragging] = useState(false);

  const ToolContextState = useToolContext();

  const [isTouchDevice, setIsTouchDevice] = useState<boolean | null>(null);
  const [isSafari, setIsSafari] = useState(false);

  const onDrop = (event: DragSyntheticEvent) => {
    // Prevent user from pushing an element into the canvas when scrolling the ItemMenu.
    if (ToolContextState.preventClickBeforeScroll) return;

    const zoomOffsets = {
      x: -75,
      y: -80,
    };

    const amountOfZooms = (canvas.getZoom() - 1) / 0.25;

    // Offset hack to counter coordinates when the canvas is panned
    const viewportOffsetX =
      canvas.viewportTransform[4] + -(zoomOffsets.x * amountOfZooms);
    const viewportOffsetY =
      canvas.viewportTransform[5] + -(zoomOffsets.y * amountOfZooms);

    const { width, height } = dynamicAsset!.dataset;

    const {
      name,
      variation,
      color,
      src,
      defaultIcon,
      alternateIcon,
      fontColor,
    } = dynamicAsset!.dataset;

    let left;
    let top;

    if ((dynamicAsset as HTMLElement & { draggable: boolean })?.draggable) {
      const { x, y } = canvas.getScenePoint(event as unknown as TPointerEvent);
      left = x + (canvas.getZoom() - 1) * 70 - viewportOffsetX / 5.5;
      top = y + (canvas.getZoom() - 1) * 70 - viewportOffsetY / 4.5;
    } else {
      left = canvas.getWidth() / 2;

      top = dynamicAsset!.dataset.variation!.includes('goal')
        ? canvas.backgroundImage!.aCoords.tl.y -
          Number(dynamicAsset!.dataset.height) / 2
        : canvas.getHeight() / 2;
    }

    if (variation?.includes('shirt')) {
      addShirt({
        name: name!,
        left,
        top,
        variation,
        color,
        canvas,
        src: src!,
        width,
        height,
        defaultIcon,
        alternateIcon,
        iconType,
        fontColor,
        scaleFactor: 0.95,
        theme: ToolContextState.theme,
      });
    } else if (!variation?.includes('line')) {
      addItem({
        name: name!,
        left,
        top,
        variation,
        color,
        canvas,
        src: src!,
        width,
        height,
        defaultIcon,
        alternateIcon,
        iconType,
        scaleFactor: 0.95,
        theme: ToolContextState.theme,
      });
    }

    // Turn off the Item Menu after placing item
    if (ToolContextState?.isMenuActive) {
      ToolContextState?.setIsMenuActive(false);
    }
  };

  const transitionBack = () => {
    if (!(dynamicAsset as HTMLElement & { draggable: boolean })?.draggable)
      return;
    setIsDragging(false);
    setTimeout(() => {
      (dynamicAsset!.firstElementChild as HTMLElement).classList.remove(
        'Controls-img--active',
      );
      // Safari FIX, can't drag items out of a scrollable container
      if (isSafari) asideDrag('scroll');
    }, 10);
    (dynamicAsset!.firstElementChild as HTMLElement).style.cssText = '';
  };

  const handleDown = (event: DragSyntheticEvent) => {
    if (!event.target.dataset.name) return;

    // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- `dynamicAsset` is the module-scoped drag handle, deliberately shared across the down/move/up handlers (see top of file)
    dynamicAsset = event.target;
    setIsDragging(
      (dynamicAsset as HTMLElement & { draggable: boolean }).draggable,
    );
    if (!(dynamicAsset as HTMLElement & { draggable: boolean }).draggable)
      return;

    const pageX = (event.touches ? event.touches[0] : event).pageX;
    const pageY = (event.touches ? event.touches[0] : event).pageY;

    const offsetLeft =
      (dynamicAsset as HTMLElement & { width: number }).width / 2;
    const offsetTop = event.touches
      ? (dynamicAsset as HTMLElement & { height: number }).height + 10
      : (dynamicAsset as HTMLElement & { height: number }).height / 2;
    const scale =
      Number(dynamicAsset?.dataset?.width) /
      (dynamicAsset!.firstElementChild as HTMLElement & { width: number })
        .width;

    (dynamicAsset!.firstElementChild as HTMLElement).style.left =
      `${pageX - offsetLeft}px`;
    (dynamicAsset!.firstElementChild as HTMLElement).style.top =
      `${pageY - offsetTop}px`;
    (dynamicAsset!.firstElementChild as HTMLElement).style.transform =
      `scale(${scale})`;

    (dynamicAsset!.firstElementChild as HTMLElement).classList.add(
      'Controls-img--active',
    );

    // Safari FIX, can't drag items out of a scrollable container
    if (isSafari) asideDrag('hidden');
  };

  const handleMove = (event: DragSyntheticEvent) => {
    if (!isDragging) return;
    if (!(dynamicAsset as HTMLElement & { draggable: boolean })?.draggable)
      return;

    const pageX = (event.touches ? event.touches[0] : event).pageX;
    const pageY = (event.touches ? event.touches[0] : event).pageY;

    const offsetLeft =
      (dynamicAsset!.firstElementChild as HTMLElement & { width: number })
        .width / 2;
    const offsetTop = event.touches
      ? (dynamicAsset!.firstElementChild as HTMLElement & { height: number })
          .height + 10
      : (dynamicAsset!.firstElementChild as HTMLElement & { height: number })
          .height / 2;
    const scale =
      Number(dynamicAsset?.dataset?.width) /
      (dynamicAsset!.firstElementChild as HTMLElement & { width: number })
        .width;

    (dynamicAsset!.firstElementChild as HTMLElement).style.left =
      `${pageX - offsetLeft}px`;
    (dynamicAsset!.firstElementChild as HTMLElement).style.top =
      `${pageY - offsetTop}px`;
    (dynamicAsset!.firstElementChild as HTMLElement).style.transform =
      `scale(${scale})`;
  };

  const handleEnd = (event: DragSyntheticEvent) => {
    // Fire onDrop event directly if on mobile device
    if (dynamicAsset && ToolContextState.isMobileStateRef.current) {
      onDrop(event);
      // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- module-scoped drag handle (see top of file)
      dynamicAsset = undefined;
      return;
    }

    if (!isDragging) return;
    if (!(dynamicAsset as HTMLElement & { draggable: boolean })?.draggable)
      return;

    const pageX = (event.changedTouches ? event.changedTouches[0] : event)
      .pageX;
    const pageY = (event.changedTouches ? event.changedTouches[0] : event)
      .pageY;

    // need to measure width/height like so, seeing as css is used to make the canvas element responsive
    const bound = canvasEl!.getBoundingClientRect();
    const { width, height, left, top } = bound;

    if (
      pageX > left &&
      pageX < width + left &&
      pageY > top &&
      pageY < height + top
    ) {
      // this if statement ensures that the image was dropped on the canvas
      event.preventDefault();
      onDrop(event);
    }
    if (dynamicAsset!.dataset.variation!.includes('line')) {
      ToolContextState.setIsDrawingLine(false);
    }
    transitionBack();
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping -- kept inside the component alongside the other drag handlers for cohesion
  const handleDragStart = (event: DragSyntheticEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (navigator)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time device detection on mount
      setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));

    const agent = navigator.userAgent.toLowerCase();
    const isTouchDevice =
      'ontouchstart' in document.documentElement ||
      /(iphone|ipod|ipad)/.test(agent) ||
      /(android)/.test(agent) ||
      /(iemobile)/.test(agent) ||
      /iphone/i.test(agent) ||
      /ipad/i.test(agent) ||
      /ipod/i.test(agent) ||
      /blackberry/i.test(agent) ||
      /bada/i.test(agent);
    setIsTouchDevice(isTouchDevice);
  }, []);

  return (
    <>
      {isTouchDevice ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the <main> is the event-delegation root for the pointer-gesture drag-and-drop of items onto the canvas; that gesture has no keyboard equivalent
        <main
          className="Main"
          onDragStart={handleDragStart as unknown as React.DragEventHandler}
          onTouchStart={handleDown as unknown as React.TouchEventHandler}
          onTouchMove={handleMove as unknown as React.TouchEventHandler}
          onTouchEnd={handleEnd as unknown as React.TouchEventHandler}
        >
          {children}
        </main>
      ) : (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the <main> is the event-delegation root for the pointer-gesture drag-and-drop of items onto the canvas; that gesture has no keyboard equivalent
        <main
          className="Main"
          onMouseDown={handleDown as unknown as React.MouseEventHandler}
          onMouseMove={handleMove as unknown as React.MouseEventHandler}
          onMouseUp={handleEnd as unknown as React.MouseEventHandler}
          onDragStart={handleDragStart as unknown as React.DragEventHandler}
        >
          {children}
        </main>
      )}
    </>
  );
};

export default Main;
